import BarFill from '@/components/charts/BarFill'
import SkeletonGroup from '@/components/motion/SkeletonGroup'
import EmptyState from '@/components/ui/EmptyState'
import SectionHead from '@/components/ui/SectionHead'
import SeeAllLink from '@/components/ui/SeeAllLink'
import Skeleton from '@/components/ui/Skeleton'
import { useRailBlock } from '@/features/market-rail/useRailFit'
import HappeningBlock from '@/features/portfolio-rail/components/HappeningBlock'
import { useHealth } from '@/features/portfolio/api/queries'
import { cn } from '@/lib/utils'
import {
  HEALTH_STRIP_ORDER as STRIP_ORDER,
  healthHeadline,
} from '@/screens/portfolio/components/health/healthPresentation'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'
import type { HealthStatus } from '@/schemas/portfolio'

/**
 * The rail's bar inks: the mock's base grades (`--up-base` / `--warn-base` / `--down-base`), a
 * step deeper than the status text the Health page's dots use, since a 6px bar is all fill.
 */
const RAIL_BAR: Record<HealthStatus, string> = {
  steady: 'bg-success-fill',
  watch: 'bg-warning-fill',
  act: 'bg-destructive-fill',
}

/**
 * Reminders (mock `AW`: "Tomorrow · NABIL book close", "In 6 days · GBIME buy still needs a
 * price"). NO BACKEND: no reminders feed exists among the v5 routes (ask P3 in
 * `mm-rest-2026-10-29/asks/pf1.md`). Parts are derivable — a book closure from `/income/`, a
 * missing price from `price_status` — but the ranked list itself has no source, so the block
 * keeps its head and says so rather than half-inventing it.
 */
function RemindersBlock() {
  const block = useRailBlock('reminders')
  return (
    <section aria-label="Reminders" {...block}>
      <SectionHead size="sm" title="Reminders" />
      <div className="mt-2">
        <EmptyState kind="not-available" thing="Reminders" size="inline" />
      </div>
    </section>
  )
}

/** Sized against the loaded block: the bar and its one line. */
function HealthSkeleton() {
  return (
    <SkeletonGroup className="flex flex-col gap-2">
      <Skeleton className="h-2 w-full rounded-none" />
      <Skeleton className="h-4 w-40" />
    </SkeletonGroup>
  )
}

/**
 * Portfolio health, as the mock's rail draws it (`pfa-rail__health`): the title, one 6px square
 * bar whose segments grow in one after another, and "{steady} of {n} checks look steady". The
 * "See more ▸" link to the Health checkup page is ours, kept by the owner (P1); the mock has no
 * page to link to.
 *
 * `GET /health/` is its own gate: a slow read here never holds back the page beside it. The count
 * is the payload's own `checks.length`, so the bar and the sentence can never disagree, and there
 * is no score — `healthReportSchema` says a client that shows one is inventing it.
 */
function HealthBlock() {
  const block = useRailBlock('health')
  const shareholderId = useActiveShareholderId()
  const healthQuery = useHealth(shareholderId)
  const { data: report, isPending, isError, isFetching } = healthQuery

  // 16/600, a step over the two list heads above it (mock `t16 sb`), so not `SectionHead sm`.
  const head = (
    <div className="flex items-center gap-2">
      <h2 className="flex-1 text-h-m">Portfolio health</h2>
      <SeeAllLink to="/portfolio/health" size="rail" aria-label="See more portfolio health checks">
        See more
      </SeeAllLink>
    </div>
  )

  const body = isPending ? (
    <HealthSkeleton />
  ) : isError ? (
    <EmptyState
      kind="failed"
      size="inline"
      title="The checkup didn't load"
      description="Nothing else on this page is affected."
      onRetry={() => {
        void healthQuery.refetch()
      }}
      retrying={isFetching}
    />
  ) : // `checks: []` means no shareholder profile at all, not an empty-but-real book (which
  // answers with `checks.length` steady checks). Branch on the length, never on `steady === 0`.
  report.checks.length === 0 ? (
    <p className="text-body-xs text-muted-foreground">
      Create a profile and Mitra will check it over for you.
    </p>
  ) : (
    <div className="flex flex-col gap-2">
      {/* Each segment's WIDTH is its count over `checks.length`, so the bar never draws a share
          the sentence under it does not say; `aria-hidden` since that sentence says it in words. */}
      <div className="flex h-2 w-full overflow-hidden" aria-hidden="true">
        {STRIP_ORDER.map((status, index) => {
          const n =
            status === 'steady' ? report.steady : status === 'watch' ? report.watch : report.act
          if (n === 0) return null
          return (
            // The kit's bar fill: grows from the left on mount; `index` × 2 steps the kit's 40ms
            // stagger to the mock's 80 between segments.
            <BarFill
              as="span"
              key={status}
              index={index * 2}
              className={RAIL_BAR[status]}
              style={{ flexGrow: n }}
            />
          )
        })}
      </div>
      <p className="text-body-xs text-muted-foreground">{healthHeadline(report)}</p>
    </div>
  )

  return (
    <section aria-label="Portfolio health" {...block}>
      {head}
      <div className="mt-2">{body}</div>
    </section>
  )
}

/**
 * The Portfolio rail (mock `AW`, on every Portfolio page): What's happening → Reminders →
 * Portfolio health, 40 apart, straight on the page. `ScreenLayout` picks it by the shell's URL
 * (`isPortfolioRoute`), as it picks the Broker rail under `/brokers`, so every Portfolio page —
 * Overview with or without a book, Holdings, the pf2 tabs, their pending skeletons and error pages —
 * draws it with nothing to pass. The import and Mitra AI pages are full width and draw no rail.
 *
 * Its own `<aside>`, named, as `MarketRail` is. On a window too short for all three, the layout's
 * fit drops Reminders, then What's happening, before Portfolio health (`RAIL_DROP_ORDER`).
 */
export default function PortfolioRail({ className }: { className?: string }) {
  return (
    <aside aria-label="Portfolio rail" className={cn('flex flex-col gap-10', className)}>
      <HappeningBlock />
      <RemindersBlock />
      <HealthBlock />
    </aside>
  )
}
